import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import API_URL from "../../apiConfig";

const COPY = {
  es: {
    locale: "es-ES",
    lang: "es",
    fallbackTitle: "ZEDRO News",
    fallbackDescription:
      "Una carta editorial de ZEDRO Academy para vivir, decidir y construir con intención.",
    pageSuffix: "ZEDRO News",
    canonicalBase: "/zedro-news",
    editionLabel: "EDICIÓN",
    tagline: "Formación con raíz eterna",
    rights: "Todos los derechos reservados.",
    communityFooter:
      "Recibes este contenido porque haces parte de la comunidad de ZEDRO Academy.",
    loadingTitle: "Cargando edición…",
    loadingText: "Estamos preparando esta carta editorial.",
    errorTitle: "No pudimos cargar esta edición",
    backText: "Volver a ZEDRO News",
    backPath: "/zedro-news",
    download: "Descargar PDF",
    preparingPdf: "Preparando PDF…",
  },
  fr: {
    locale: "fr-CA",
    lang: "fr",
    fallbackTitle: "ZEDRO News",
    fallbackDescription:
      "Une lettre éditoriale de ZEDRO Academy pour vivre, décider et construire avec intention.",
    pageSuffix: "ZEDRO News",
    canonicalBase: "/fr/zedro-news",
    editionLabel: "ÉDITION",
    tagline: "Formation à racine éternelle",
    rights: "Tous droits réservés.",
    communityFooter:
      "Vous recevez ce contenu parce que vous faites partie de la communauté de ZEDRO Academy.",
    loadingTitle: "Chargement de l’édition…",
    loadingText: "Nous préparons cette lettre éditoriale.",
    errorTitle: "Nous n’avons pas pu charger cette édition",
    backText: "Retour à ZEDRO News",
    backPath: "/fr/zedro-news",
    download: "Télécharger le PDF",
    preparingPdf: "Préparation du PDF…",
  },
};

function cleanLang(value = "es") {
  return String(value || "").trim().toLowerCase() === "fr" ? "fr" : "es";
}

function getCopy(language = "es") {
  return COPY[cleanLang(language)] || COPY.es;
}

function decodeEntities(value = "") {
  if (typeof document === "undefined") return String(value || "");

  const textarea = document.createElement("textarea");
  textarea.innerHTML = String(value || "");
  return textarea.value;
}

function normalizeText(value = "") {
  return decodeEntities(value)
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<\/(p|div|section|article|blockquote|h[1-6]|li)>/gi, "\n\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/\r\n?/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/[•]/g, "-")
    .replace(/([.!?:;])(?=[A-Za-zÁÉÍÓÚÜÑáéíóúüñ¿¡])/g, "$1 ")
    .replace(/[ \t]+/g, " ")
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function stripHtml(value = "") {
  return normalizeText(value).replace(/\s+/g, " ").trim();
}

function truncate(value = "", max = 155) {
  const clean = stripHtml(value);
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trim()}…`;
}

function isLegacyPlaceholder(value = "") {
  const clean = stripHtml(value).toUpperCase();
  return ["VER", "ELEGIR", "ELIMINAR"].includes(clean);
}

function safeText(value = "") {
  if (isLegacyPlaceholder(value)) return "";
  return normalizeText(value);
}

function hasText(value = "") {
  return safeText(value).trim().length > 0;
}

function hasAnyText(...values) {
  return values.some((value) => hasText(value));
}

function splitParagraphs(value = "") {
  return safeText(value)
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);
}


function pdfSafeText(value = "") {
  return safeText(value)
    .replace(/👉|➡️|➡|➜|→/g, "-")
    .replace(/✅|✔️|✔|☑️|☑/g, "-")
    .replace(/…/g, "...")
    .replace(/([^\s])([.!?:;])(?=\S)/g, "$1$2 ")
    .replace(/([a-záéíóúüñ])([A-ZÁÉÍÓÚÜÑ])/g, "$1 $2")
    .replace(/[^\n\x20-\x7E\xA1-\xFF]/g, "")
    .replace(/[ \t]+/g, " ")
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function splitPdfParagraphs(value = "") {
  return pdfSafeText(value)
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}


function splitLines(value = "") {
  return safeText(value)
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function getIssueTitle(issue = {}, copy = COPY.es) {
  return (
    issue.meta_title ||
    issue.seo_title ||
    issue.hero_title ||
    issue.title ||
    copy.fallbackTitle
  );
}

function getIssueDescription(issue = {}, copy = COPY.es) {
  return truncate(
    issue.meta_description ||
      issue.seo_description ||
      issue.hero_sub ||
      issue.lead ||
      issue.idea_central ||
      issue.summary ||
      copy.fallbackDescription
  );
}

function getIssueImage(issue = {}) {
  return issue.hero_image || issue.cover_url || issue.image_url || "";
}

function getIssueNumber(issue = {}) {
  const value = issue.issue_line || issue.edition || "";
  const match = String(value || "").match(/\d+/);
  return match?.[0]?.padStart(4, "0") || "0001";
}

function formatIssueDate(value, copy = COPY.es) {
  const source = value ? new Date(value) : new Date();
  const safeDate = Number.isNaN(source.getTime()) ? new Date() : source;

  return safeDate.toLocaleDateString(copy.locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function setMetaTag(name, content) {
  if (!content) return;

  let tag = document.querySelector(`meta[name="${name}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function setPropertyTag(property, content) {
  if (!content) return;

  let tag = document.querySelector(`meta[property="${property}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function loadImageElement(src, crossOrigin = null) {
  return new Promise((resolve) => {
    const image = new Image();
    if (crossOrigin) image.crossOrigin = crossOrigin;

    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

function imageElementToJpegData(image) {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width || 1;
    canvas.height = image.naturalHeight || image.height || 1;

    const context = canvas.getContext("2d");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0);

    return {
      dataUrl: canvas.toDataURL("image/jpeg", 0.92),
      width: canvas.width,
      height: canvas.height,
      format: "JPEG",
    };
  } catch (_error) {
    return null;
  }
}

async function getImageData(src) {
  if (!src || typeof window === "undefined") return null;

  const cleanSrc = String(src || "").trim();
  if (!cleanSrc) return null;

  let absoluteSrc = cleanSrc;
  try {
    absoluteSrc = new URL(cleanSrc, window.location.href).href;
  } catch (_error) {
    absoluteSrc = cleanSrc;
  }

  try {
    const proxyResponse = await fetch(
      `${API_URL}/api/editorials/image-base64?url=${encodeURIComponent(absoluteSrc)}`
    );
    const proxyJson = await proxyResponse.json().catch(() => null);

    if (proxyResponse.ok && proxyJson?.dataUrl) {
      const proxyImage = await loadImageElement(proxyJson.dataUrl);
      if (proxyImage) {
        const converted = imageElementToJpegData(proxyImage);
        if (converted) return converted;
      }
    }
  } catch (_error) {
    // If the backend proxy is not available yet, try direct loading below.
  }

  try {
    const response = await fetch(absoluteSrc, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      cache: "force-cache",
    });

    if (response.ok) {
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const image = await loadImageElement(objectUrl);
      URL.revokeObjectURL(objectUrl);

      if (image) {
        const converted = imageElementToJpegData(image);
        if (converted) return converted;
      }
    }
  } catch (_error) {
    // If the image host blocks fetch, try normal image loading below.
  }

  const fallbackImage = await loadImageElement(absoluteSrc, "anonymous");
  if (!fallbackImage) return null;

  return imageElementToJpegData(fallbackImage);
}

async function generateNewsletterPdf(issue = {}, language = "es") {
  const copy = getCopy(language);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const page = {
    width: 210,
    height: 297,
    marginX: 20,
    top: 34,
    bottom: 26,
    contentWidth: 170,
  };

  const color = {
    navy: [15, 47, 70],
    navySoft: [35, 78, 107],
    gold: [207, 174, 85],
    paleGold: [244, 210, 122],
    ink: [32, 48, 64],
    muted: [105, 119, 137],
    line: [222, 228, 235],
    soft: [247, 249, 252],
    white: [255, 255, 255],
  };

  const title = pdfSafeText(getIssueTitle(issue, copy)) || "ZEDRO News";
  const subtitle = pdfSafeText(issue.hero_sub || issue.summary || "");
  const issueNumber = getIssueNumber(issue);
  const issueDate = formatIssueDate(
    issue.published_at || issue.created_at || issue.date || issue.updated_at,
    copy
  );

  let y = page.top;

  const setTextColor = (rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  const setFillColor = (rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);
  const setDrawColor = (rgb) => doc.setDrawColor(rgb[0], rgb[1], rgb[2]);

  const drawContentHeader = () => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    setTextColor(color.gold);
    doc.text("Z E D R O", page.marginX, 18);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    setTextColor(color.muted);
    doc.text("ZEDRO News", page.width - page.marginX, 18, { align: "right" });

    setDrawColor(color.line);
    doc.setLineWidth(0.2);
    doc.line(page.marginX, 24, page.width - page.marginX, 24);
  };

  const drawFooter = (pageNumber, totalPages) => {
    setDrawColor(color.line);
    doc.setLineWidth(0.2);
    doc.line(page.marginX, 279, page.width - page.marginX, 279);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setTextColor(color.muted);
    doc.text(
      "© 2026 Académie ZEDRO - ZEDRO Academy. " + copy.rights,
      page.marginX,
      286
    );
    doc.text(`${pageNumber} / ${totalPages}`, page.width - page.marginX, 286, {
      align: "right",
    });
  };

  const addPage = () => {
    doc.addPage();
    drawContentHeader();
    y = page.top;
  };

  const ensureSpace = (needed = 16) => {
    if (y + needed <= page.height - page.bottom) return;
    addPage();
  };

  const addWrappedText = ({
    text,
    x = page.marginX,
    maxWidth = page.contentWidth,
    fontSize = 11,
    lineHeight = 6.3,
    style = "normal",
    textColor = color.ink,
    align = "left",
    after = 4,
  }) => {
    const clean = pdfSafeText(text);
    if (!clean) return;

    const applyTextStyle = () => {
      doc.setFont("helvetica", style);
      doc.setFontSize(fontSize);
      doc.setCharSpace(0);
      setTextColor(textColor);
    };

    applyTextStyle();

    const lines = clean
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .flatMap((line) => doc.splitTextToSize(line, maxWidth));

    if (!lines.length) return;

    const textX = align === "center" ? x + maxWidth / 2 : x;

    lines.forEach((line) => {
      ensureSpace(lineHeight + 2);
      applyTextStyle();
      doc.text(line, textX, y, { align, maxWidth });
      y += lineHeight;
    });

    y += after;
  };

  const addHeading = (text) => {
    const clean = safeText(text);
    if (!clean) return;

    y += 2;
    addWrappedText({
      text: clean,
      fontSize: 16,
      lineHeight: 7.8,
      style: "bold",
      textColor: color.navy,
      after: 4,
    });
  };

  const addParagraphs = (value, options = {}) => {
    const paragraphs = splitPdfParagraphs(value);
    if (!paragraphs.length) return;

    paragraphs.forEach((paragraph, index) => {
      addWrappedText({
        text: paragraph,
        fontSize: options.lead ? 12 : 10.8,
        lineHeight: options.lead ? 7.1 : 6.5,
        textColor: options.lead ? color.navy : color.ink,
        after: index === paragraphs.length - 1 ? 5 : 3.4,
      });
    });
  };

  const addDivider = () => {
    ensureSpace(12);
    y += 3;
    setDrawColor(color.line);
    doc.setLineWidth(0.2);
    doc.line(page.marginX, y, page.width - page.marginX, y);
    y += 9;
  };

  const addQuote = (value) => {
    const clean = pdfSafeText(value);
    if (!clean) return;

    const lines = doc.splitTextToSize(`"${clean}"`, page.contentWidth - 18);
    const boxHeight = Math.max(22, lines.length * 6.7 + 14);
    ensureSpace(boxHeight + 7);

    setFillColor([253, 248, 235]);
    setDrawColor([230, 204, 139]);
    doc.roundedRect(page.marginX, y, page.contentWidth, boxHeight, 4, 4, "FD");

    doc.setFont("helvetica", "italic");
    doc.setFontSize(12);
    setTextColor(color.navy);
    doc.text(lines, page.marginX + 9, y + 10, { maxWidth: page.contentWidth - 18 });
    y += boxHeight + 9;
  };

  const addImage = async (src, caption = "") => {
    const image = await getImageData(src);
    if (!image) return;

    const maxWidth = page.contentWidth;
    const maxHeight = 86;
    const ratio = image.width / image.height || 1.5;
    let drawWidth = maxWidth;
    let drawHeight = drawWidth / ratio;

    if (drawHeight > maxHeight) {
      drawHeight = maxHeight;
      drawWidth = drawHeight * ratio;
    }

    ensureSpace(drawHeight + (caption ? 14 : 8));

    const x = page.marginX + (maxWidth - drawWidth) / 2;
    doc.addImage(image.dataUrl, image.format, x, y, drawWidth, drawHeight);
    y += drawHeight + 5;

    if (caption) {
      addWrappedText({
        text: caption,
        x: page.marginX + 14,
        maxWidth: page.contentWidth - 28,
        fontSize: 8.8,
        lineHeight: 4.8,
        textColor: color.muted,
        align: "center",
        after: 4,
      });
    }
  };

  const addListOrReflection = (value) => {
    const lines = pdfSafeText(value)
      .split(/\n+/)
      .map((item) => item.trim())
      .filter(Boolean);
    const paragraphs = splitPdfParagraphs(value);
    if (!lines.length) return;

    if (lines.length >= 2 && paragraphs.length <= 1) {
      ensureSpace(18);
      lines.forEach((line) => {
        addWrappedText({
          text: `- ${line.replace(/^-\s*/, "")}`,
          x: page.marginX + 5,
          maxWidth: page.contentWidth - 5,
          fontSize: 10.7,
          lineHeight: 6.4,
          textColor: color.ink,
          after: 2.5,
        });
      });
      y += 3;
      return;
    }

    addParagraphs(value);
  };

  const addNoteBox = (value) => {
    const paragraphs = splitPdfParagraphs(value);
    if (!paragraphs.length) return;

    const boxWidth = page.contentWidth - 14;
    const boxX = page.marginX + 7;
    const textWidth = boxWidth - 18;
    const lineHeight = 5.8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.2);
    doc.setCharSpace(0);

    const lineGroups = paragraphs.map((paragraph) =>
      doc.splitTextToSize(paragraph, textWidth)
    );
    const textHeight = lineGroups.reduce(
      (sum, lines, index) => sum + lines.length * lineHeight + (index ? 2.4 : 0),
      0
    );
    const boxHeight = Math.max(24, textHeight + 15);

    ensureSpace(boxHeight + 8);

    setFillColor(color.soft);
    setDrawColor(color.line);
    doc.roundedRect(boxX, y, boxWidth, boxHeight, 4, 4, "FD");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.2);
    doc.setCharSpace(0);
    setTextColor(color.ink);

    let innerY = y + 9.5;
    lineGroups.forEach((lines, groupIndex) => {
      if (groupIndex) innerY += 2.4;
      lines.forEach((line) => {
        doc.text(line, boxX + 9, innerY, { maxWidth: textWidth });
        innerY += lineHeight;
      });
    });

    y += boxHeight + 9;
  };

  const addCtaBox = () => {
    const bodyParagraphs = splitPdfParagraphs(issue.practica);
    const button = pdfSafeText(issue.step4);
    if (!bodyParagraphs.length && !button) return;

    const boxWidth = 132;
    const boxX = page.marginX + (page.contentWidth - boxWidth) / 2;
    const textWidth = boxWidth - 24;
    const lineHeight = 4.75;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.9);
    doc.setCharSpace(0);

    const lineGroups = bodyParagraphs.map((paragraph) =>
      doc.splitTextToSize(paragraph, textWidth)
    );
    const bodyHeight = lineGroups.reduce(
      (sum, lines, index) => sum + lines.length * lineHeight + (index ? 1.25 : 0),
      0
    );
    const buttonHeight = button ? 8.5 : 0;
    const boxHeight = Math.max(
      28,
      10 + bodyHeight + (button ? 6 + buttonHeight : 0) + 7
    );

    ensureSpace(boxHeight + 9);

    setFillColor(color.navy);
    setDrawColor(color.gold);
    doc.setLineWidth(0.22);
    doc.roundedRect(boxX, y, boxWidth, boxHeight, 5, 5, "FD");

    let innerY = y + 9.5;

    if (lineGroups.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.9);
      doc.setCharSpace(0);
      setTextColor(color.white);
      lineGroups.forEach((lines, groupIndex) => {
        if (groupIndex) innerY += 1.25;
        lines.forEach((line) => {
          doc.text(line, boxX + 12, innerY, { maxWidth: textWidth });
          innerY += lineHeight;
        });
      });
      innerY += 4.8;
    }

    if (button) {
      const buttonWidth = Math.min(58, Math.max(34, doc.getTextWidth(button) + 17));
      const buttonX = boxX + boxWidth / 2 - buttonWidth / 2;
      setFillColor(color.paleGold);
      doc.roundedRect(buttonX, innerY - 4.8, buttonWidth, buttonHeight, 4.5, 4.5, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.4);
      doc.setCharSpace(0);
      setTextColor(color.navy);
      doc.text(button, boxX + boxWidth / 2, innerY + 1.05, { align: "center" });
    }

    y += boxHeight + 9;
  };

  setFillColor(color.soft);
  doc.rect(0, 0, page.width, page.height, "F");

  setFillColor(color.navy);
  doc.roundedRect(24, 22, 162, 52, 8, 8, "F");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.8);
  setTextColor([255, 255, 255]);
  doc.text(`${copy.editionLabel} ${issueNumber}`, 34, 32);
  doc.text(issueDate.toUpperCase(), 176, 32, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  setTextColor(color.gold);
  doc.text("Z  E  D  R  O", page.width / 2, 50, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.2);
  setTextColor([255, 255, 255]);
  doc.text(copy.tagline, page.width / 2, 58, { align: "center" });

  setDrawColor(color.gold);
  doc.roundedRect(81, 64, 48, 8, 5, 5, "S");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  setTextColor(color.paleGold);
  doc.text("ZEDRO News", page.width / 2, 69.5, { align: "center" });

  let coverY = 98;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  setTextColor(color.navy);
  const titleLines = doc.splitTextToSize(title, 154);
  doc.text(titleLines, page.width / 2, coverY, { align: "center", maxWidth: 154 });
  coverY += titleLines.length * 10 + 8;

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    setTextColor(color.navySoft);
    const subtitleLines = doc.splitTextToSize(subtitle, 146);
    doc.text(subtitleLines, page.width / 2, coverY, {
      align: "center",
      maxWidth: 146,
    });
    coverY += subtitleLines.length * 6.8 + 12;
  }

  const heroImage = await getImageData(getIssueImage(issue));
  if (heroImage) {
    const maxWidth = 154;
    const maxHeight = 72;
    const ratio = heroImage.width / heroImage.height || 1.55;
    let drawWidth = maxWidth;
    let drawHeight = drawWidth / ratio;

    if (drawHeight > maxHeight) {
      drawHeight = maxHeight;
      drawWidth = drawHeight * ratio;
    }

    const imageX = page.width / 2 - drawWidth / 2;
    const imageY = Math.min(coverY + 6, 188);

    doc.addImage(heroImage.dataUrl, heroImage.format, imageX, imageY, drawWidth, drawHeight);
  }

  addPage();

  if (hasText(issue.lead)) {
    addParagraphs(issue.lead, { lead: true });
    addDivider();
  }

  if (hasText(issue.idea_title)) {
    addHeading(issue.idea_title);
  }

  if (hasText(issue.idea_central) || issue.idea_image) {
    addParagraphs(issue.idea_central);
    await addImage(issue.idea_image, issue.idea_image_caption);
    addDivider();
  }

  if (
    hasText(issue.bloque1_title) ||
    hasText(issue.bloque1_p1) ||
    hasText(issue.quote_text) ||
    hasText(issue.bloque1_p2) ||
    issue.inline_image
  ) {
    addHeading(issue.bloque1_title);
    addParagraphs(issue.bloque1_p1);
    addQuote(issue.quote_text);
    addParagraphs(issue.bloque1_p2);
    await addImage(issue.inline_image, issue.inline_caption);
    addDivider();
  }

  if (hasText(issue.callout_title) || hasText(issue.callout_text)) {
    addHeading(issue.callout_title);
    addParagraphs(issue.callout_text);
    addDivider();
  }

  if (hasText(issue.step1) || hasText(issue.step2)) {
    addHeading(issue.step1);
    addParagraphs(issue.step2);
    addDivider();
  }

  if (hasText(issue.step3)) {
    addListOrReflection(issue.step3);
    addDivider();
  }

  if (hasText(issue.callout_body)) {
    addNoteBox(issue.callout_body);
  }

  if (hasText(issue.cierre_title) || hasText(issue.cierre_text)) {
    addHeading(issue.cierre_title);
    addParagraphs(issue.cierre_text);
  }

  addCtaBox();

  const totalPages = doc.internal.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    doc.setPage(pageNumber);
    drawFooter(pageNumber, totalPages);
  }

  const fileBase =
    pdfSafeText(issue.slug || title)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "zedro-news";

  doc.save(`${fileBase}.pdf`);
}

function PublicParagraphs({ value, variant = "body" }) {
  const paragraphs = splitParagraphs(value);
  if (!paragraphs.length) return null;

  const style =
    variant === "lead"
      ? styles.leadText
      : variant === "cta"
        ? styles.ctaText
        : styles.bodyText;

  const className =
    variant === "lead"
      ? "zedroIssueLeadText"
      : variant === "cta"
        ? "zedroIssueCtaText"
        : "zedroIssueBodyText";

  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <p key={index} className={className} style={style}>
          {paragraph}
        </p>
      ))}
    </>
  );
}

function PublicListOrReflection({ value }) {
  const lines = splitLines(value);
  const paragraphs = splitParagraphs(value);

  if (!lines.length) return null;

  if (lines.length >= 2 && paragraphs.length <= 1) {
    return (
      <ul className="zedroIssueCleanList" style={styles.cleanList}>
        {lines.map((line, index) => (
          <li key={index}>{line.replace(/^-\s*/, "")}</li>
        ))}
      </ul>
    );
  }

  return <PublicParagraphs value={value} />;
}

function PublicImage({ src, caption, alt = "" }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) return null;

  return (
    <figure className="zedroIssueFigure" style={styles.figure}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => setFailed(true)}
        className="zedroIssueImage"
        style={styles.image}
      />

      {hasText(caption) && <figcaption className="zedroIssueCaption" style={styles.caption}>{safeText(caption)}</figcaption>}
    </figure>
  );
}

function PublicSection({ title, children, image, imageCaption, imageAlt, variant = "default" }) {
  const hasImage = Boolean(image);

  return (
    <section
      className={`zedroIssueSection ${variant === "soft" ? "zedroIssueSectionSoft" : ""}`.trim()}
      style={{
        ...styles.section,
        ...(variant === "soft" ? styles.sectionSoft : {}),
      }}
    >
      {hasText(title) && <h2 className="zedroIssueSectionTitle" style={styles.sectionTitle}>{safeText(title)}</h2>}

      <div className={hasImage ? "zedroIssueSectionGrid" : "zedroIssueSectionFull"} style={hasImage ? styles.sectionGrid : styles.sectionFull}>
        <div>{children}</div>

        {hasImage && (
          <PublicImage
            src={image}
            caption={imageCaption}
            alt={imageAlt}
          />
        )}
      </div>
    </section>
  );
}

function PublicNewsletterArticle({ issue = {}, language = "es" }) {
  const copy = getCopy(language);
  const issueNumber = getIssueNumber(issue);
  const issueDate = formatIssueDate(
    issue.published_at || issue.created_at || issue.date || issue.updated_at,
    copy
  );

  const hasMainDevelopment = hasAnyText(
    issue.bloque1_title,
    issue.bloque1_p1,
    issue.quote_text,
    issue.bloque1_p2,
    issue.inline_image
  );
  const hasAdditionalBlock = hasAnyText(issue.callout_title, issue.callout_text);
  const hasSecondAdditionalBlock = hasAnyText(issue.step1, issue.step2);
  const hasListOrReflection = hasText(issue.step3);
  const hasNotes = hasText(issue.callout_body);
  const hasCta = hasAnyText(issue.practica, issue.step4);

  return (
    <article className="zedroIssueWrapper" style={styles.wrapper}>
      <header className="zedroIssueBrandHeader" style={styles.brandHeader}>
        <div className="zedroIssueBrandInner" style={styles.brandInner}>
          <div className="zedroIssueBrandTopline" style={styles.brandTopline}>
            <span>{copy.editionLabel} {issueNumber}</span>
            <span>{issueDate}</span>
          </div>

          <div style={styles.brandCenter}>
            <div className="zedroIssueLogoWordmark" style={styles.logoWordmark}>Z&nbsp;&nbsp;E&nbsp;&nbsp;D&nbsp;&nbsp;R&nbsp;&nbsp;O</div>
            <div className="zedroIssueLogoTagline" style={styles.logoTagline}>{copy.tagline}</div>
            <div className="zedroIssueNewsLabel" style={styles.newsLabel}>ZEDRO News</div>
          </div>
        </div>
      </header>

      <section className="zedroIssueHero" style={styles.hero}>
        <h1 className="zedroIssueHeroTitle" style={styles.heroTitle}>
          {safeText(issue.hero_title || issue.title) || "Título principal de la edición"}
        </h1>

        {hasAnyText(issue.hero_sub, issue.summary) && (
          <p className="zedroIssueHeroSub" style={styles.heroSub}>{safeText(issue.hero_sub || issue.summary)}</p>
        )}

        <PublicImage
          src={issue.hero_image || issue.cover_url}
          alt={safeText(issue.hero_title || issue.title) || "Imagen principal"}
        />
      </section>

      {hasText(issue.lead) && (
        <PublicSection variant="soft">
          <PublicParagraphs value={issue.lead} variant="lead" />
        </PublicSection>
      )}

      {hasAnyText(issue.idea_title, issue.idea_central, issue.idea_image) && (
        <PublicSection
          title={issue.idea_title}
          image={issue.idea_image}
          imageCaption={issue.idea_image_caption}
          imageAlt={issue.idea_title || "Imagen de idea central"}
        >
          <PublicParagraphs value={issue.idea_central} />
        </PublicSection>
      )}

      {hasMainDevelopment && (
        <PublicSection
          title={issue.bloque1_title}
          image={issue.inline_image}
          imageCaption={issue.inline_caption}
          imageAlt={issue.bloque1_title || "Imagen editorial"}
        >
          <PublicParagraphs value={issue.bloque1_p1} />

          {hasText(issue.quote_text) && (
            <blockquote className="zedroIssueQuoteBox" style={styles.quoteBox}>
              “{safeText(issue.quote_text)}”
            </blockquote>
          )}

          <PublicParagraphs value={issue.bloque1_p2} />
        </PublicSection>
      )}

      {hasAdditionalBlock && (
        <PublicSection title={issue.callout_title} variant="soft">
          <PublicParagraphs value={issue.callout_text} />
        </PublicSection>
      )}

      {hasSecondAdditionalBlock && (
        <PublicSection title={issue.step1}>
          <PublicParagraphs value={issue.step2} />
        </PublicSection>
      )}

      {hasListOrReflection && (
        <PublicSection variant="soft">
          <PublicListOrReflection value={issue.step3} />
        </PublicSection>
      )}

      {hasNotes && (
        <PublicSection>
          <div className="zedroIssueNoteBox" style={styles.noteBox}>
            <PublicParagraphs value={issue.callout_body} />
          </div>
        </PublicSection>
      )}

      {hasAnyText(issue.cierre_title, issue.cierre_text) && (
        <PublicSection title={issue.cierre_title}>
          <PublicParagraphs value={issue.cierre_text} />
        </PublicSection>
      )}

      {hasCta && (
        <section className="zedroIssueCta" style={styles.ctaEditorialSection}>
          <PublicParagraphs value={issue.practica} variant="cta" />

          {hasText(issue.step4) && (
            <div style={styles.ctaActions}>
              <a href="/store" style={styles.ctaButton}>
                {safeText(issue.step4)}
              </a>
            </div>
          )}
        </section>
      )}

      <footer className="zedroIssueFooter" style={styles.footer}>
        <p style={styles.footerBrand}>ZEDRO Academy</p>
        <p>© 2026 Académie ZEDRO — ZEDRO Academy. {copy.rights}</p>
        <p>{copy.communityFooter}</p>
      </footer>
    </article>
  );
}


const issueResponsiveStyles = `
  @media (max-width: 760px) {
    .zedroIssuePage {
      background: #ffffff !important;
      padding: 0 0 44px !important;
      overflow-x: hidden !important;
    }

    .zedroIssueDownloadRow {
      width: 100% !important;
      margin: 0 !important;
      padding: 14px 18px 12px !important;
      box-sizing: border-box !important;
      justify-content: flex-start !important;
      background: #ffffff !important;
      border-bottom: 1px solid rgba(15, 47, 70, 0.07) !important;
    }

    .zedroIssuePdfButton {
      min-height: 40px !important;
      padding: 0 16px !important;
      font-size: 14px !important;
      box-shadow: none !important;
    }

    .zedroIssueArticleShell {
      width: 100% !important;
      margin: 0 !important;
    }

    .zedroIssueWrapper {
      width: 100% !important;
      max-width: none !important;
      margin: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      background: #ffffff !important;
    }

    .zedroIssueBrandInner {
      padding: 22px 22px 26px !important;
    }

    .zedroIssueBrandTopline {
      gap: 10px !important;
      flex-wrap: wrap !important;
      margin-bottom: 22px !important;
      font-size: 10px !important;
      letter-spacing: 0.13em !important;
    }

    .zedroIssueLogoWordmark {
      font-size: 30px !important;
      letter-spacing: 0.13em !important;
    }

    .zedroIssueLogoTagline {
      font-size: 12px !important;
      margin-bottom: 18px !important;
    }

    .zedroIssueNewsLabel {
      min-height: 30px !important;
      padding: 0 15px !important;
      font-size: 14px !important;
    }

    .zedroIssueHero {
      padding: 34px 24px 28px !important;
    }

    .zedroIssueHeroTitle {
      max-width: 100% !important;
      font-size: clamp(38px, 10.4vw, 48px) !important;
      line-height: 1.08 !important;
      letter-spacing: -0.045em !important;
    }

    .zedroIssueHeroSub {
      max-width: 100% !important;
      font-size: 18px !important;
      line-height: 1.72 !important;
    }

    .zedroIssueSection {
      padding: 0 26px 36px !important;
    }

    .zedroIssueSectionSoft {
      padding: 28px 26px 32px !important;
      background: #ffffff !important;
      border-top: 1px solid rgba(15, 47, 70, 0.06) !important;
      border-bottom: 1px solid rgba(15, 47, 70, 0.05) !important;
    }

    .zedroIssueSectionGrid {
      display: block !important;
    }

    .zedroIssueSectionTitle {
      font-size: 32px !important;
      line-height: 1.14 !important;
      letter-spacing: -0.035em !important;
      margin-bottom: 18px !important;
    }

    .zedroIssueLeadText {
      font-size: 18.5px !important;
      line-height: 1.9 !important;
      margin-bottom: 18px !important;
    }

    .zedroIssueBodyText {
      font-size: 18px !important;
      line-height: 1.9 !important;
      margin-bottom: 18px !important;
    }

    .zedroIssueFigure {
      margin: 24px 0 4px !important;
    }

    .zedroIssueImage {
      border-radius: 20px !important;
      box-shadow: 0 16px 34px rgba(15, 47, 70, 0.10) !important;
    }

    .zedroIssueCaption {
      font-size: 12.5px !important;
      line-height: 1.5 !important;
    }

    .zedroIssueQuoteBox {
      margin: 28px 0 !important;
      padding: 22px 24px !important;
      font-size: 24px !important;
      line-height: 1.45 !important;
      border-radius: 0 22px 22px 0 !important;
    }

    .zedroIssueCleanList {
      margin-top: 18px !important;
      padding: 18px 18px 18px 34px !important;
      font-size: 18px !important;
      line-height: 1.8 !important;
    }

    .zedroIssueNoteBox {
      padding: 20px 20px 6px !important;
      border-radius: 22px !important;
    }

    .zedroIssueCta {
      width: auto !important;
      margin: 4px 24px 34px !important;
      padding: 22px 22px 24px !important;
      border-radius: 24px !important;
    }

    .zedroIssueCtaText {
      font-size: 17px !important;
      line-height: 1.78 !important;
    }

    .zedroIssueFooter {
      padding: 26px 24px 34px !important;
    }
  }

  @media (max-width: 420px) {
    .zedroIssueBrandInner {
      padding: 20px 18px 24px !important;
    }

    .zedroIssueBrandTopline {
      justify-content: center !important;
      text-align: center !important;
    }

    .zedroIssueLogoWordmark {
      font-size: 28px !important;
      letter-spacing: 0.12em !important;
    }

    .zedroIssueHero {
      padding: 32px 22px 26px !important;
    }

    .zedroIssueHeroTitle {
      font-size: clamp(36px, 10.8vw, 44px) !important;
    }

    .zedroIssueHeroSub {
      font-size: 17px !important;
    }

    .zedroIssueSection {
      padding: 0 24px 34px !important;
    }

    .zedroIssueSectionSoft {
      padding: 26px 24px 30px !important;
    }

    .zedroIssueSectionTitle {
      font-size: 30px !important;
    }

    .zedroIssueLeadText,
    .zedroIssueBodyText {
      font-size: 17.5px !important;
      line-height: 1.88 !important;
    }

    .zedroIssueQuoteBox {
      font-size: 22px !important;
      padding: 20px 22px !important;
    }
  }
`;

export default function NewsletterIssuePage({ language = "es" }) {
  const copy = getCopy(language);
  const lang = cleanLang(language);
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const seo = useMemo(() => {
    const issue = data || {};
    const title = getIssueTitle(issue, copy);
    const description = getIssueDescription(issue, copy);
    const image = getIssueImage(issue);

    return {
      title,
      pageTitle: `${title} | ${copy.pageSuffix}`,
      description,
      image,
      canonical:
        typeof window !== "undefined"
          ? `${window.location.origin}${copy.canonicalBase}/${slug || ""}`
          : "",
    };
  }, [data, slug, copy]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(
          `${API_URL}/api/editorials/public/slug/${encodeURIComponent(slug)}?lang=${lang}`
        );
        const json = await res.json().catch(() => null);

        if (!res.ok || !json) {
          throw new Error(json?.error || "No se pudo cargar esta edición.");
        }

        const issue = json.issue ?? json;
        if (!cancelled) setData(issue);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (slug) run();

    return () => {
      cancelled = true;
    };
  }, [slug, lang]);

  useEffect(() => {
    if (!data) return;

    document.title = seo.pageTitle;

    setMetaTag("description", seo.description);
    setMetaTag("robots", "index, follow");

    setPropertyTag("og:title", seo.pageTitle);
    setPropertyTag("og:description", seo.description);
    setPropertyTag("og:type", "article");
    setPropertyTag("og:url", seo.canonical);

    if (seo.image) {
      setPropertyTag("og:image", seo.image);
    }

    setMetaTag("twitter:card", seo.image ? "summary_large_image" : "summary");
    setMetaTag("twitter:title", seo.pageTitle);
    setMetaTag("twitter:description", seo.description);

    if (seo.image) {
      setMetaTag("twitter:image", seo.image);
    }

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", seo.canonical);
  }, [data, seo]);

  const handleDownloadPdf = async () => {
    if (!data || pdfLoading) return;

    try {
      setPdfLoading(true);
      await generateNewsletterPdf(data, lang);
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <main style={styles.statusPage}>
        <div style={styles.statusCard}>
          <div style={styles.kicker}>ZEDRO News</div>
          <h1 style={styles.statusTitle}>{copy.loadingTitle}</h1>
<p style={styles.statusText}>{copy.loadingText}</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main style={styles.statusPage}>
        <div style={styles.statusCard}>
          <div style={styles.kicker}>ZEDRO News</div>
          <h1 style={styles.statusTitle}>{copy.errorTitle}</h1>
          <p style={{ ...styles.statusText, color: "#7a2b2b" }}>{error}</p>

          <Link to={copy.backPath} style={styles.linkButton}>
            {copy.backText}
          </Link>
        </div>
      </main>
    );
  }

  const issue = data || {};

  return (
    <main className="zedroIssuePage" style={styles.page}>
      <style>{issueResponsiveStyles}</style>
      <section className="zedroIssueDownloadRow" style={styles.downloadRow} aria-label="Opciones de la edición">
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={pdfLoading}
          className="zedroIssuePdfButton"
          style={{
            ...styles.pdfButton,
            ...(pdfLoading ? styles.pdfButtonDisabled : {}),
          }}
        >
          {pdfLoading ? copy.preparingPdf : copy.download}
        </button>
      </section>

      <div className="zedroIssueArticleShell" style={styles.article}>
        <PublicNewsletterArticle issue={issue} language={lang} />
      </div>
    </main>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #f7f9fc 0%, #ffffff 52%, #f7f9fc 100%)",
    padding: "24px 18px 52px",
  },

  downloadRow: {
    width: "min(1120px, 100%)",
    margin: "0 auto 16px",
    display: "flex",
    justifyContent: "flex-end",
  },

  pdfButton: {
    border: 0,
    minHeight: 44,
    padding: "0 20px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #d7a951 0%, #f0cf75 100%)",
    color: "#0f2f46",
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxShadow: "0 12px 28px rgba(207,174,85,.26)",
  },

  pdfButtonDisabled: {
    opacity: 0.68,
    cursor: "not-allowed",
  },

  article: {
    width: "min(1120px, 100%)",
    margin: "0 auto",
  },

  wrapper: {
    maxWidth: 920,
    margin: "0 auto",
    overflow: "hidden",
    borderRadius: 24,
    background: "#ffffff",
    color: "#203040",
    boxShadow: "0 30px 80px rgba(15,47,70,0.14)",
    fontFamily: "var(--font-body, Questrial, sans-serif)",
    lineHeight: 1.7,
  },

  brandHeader: {
    background:
      "linear-gradient(135deg, #0f2f46 0%, #173f5b 58%, #0c2638 100%)",
    padding: 0,
    color: "#ffffff",
    borderBottom: "4px solid rgba(207,174,85,.92)",
  },

  brandInner: {
    padding: "28px 46px 38px",
  },

  brandTopline: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    fontSize: 10.5,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.78)",
    marginBottom: 30,
    padding: "0 4px",
  },

  brandCenter: {
    textAlign: "center",
  },

  logoWordmark: {
    fontFamily: "var(--font-title, 'Playfair Display', serif)",
    fontSize: 34,
    letterSpacing: "0.16em",
    color: "#d7a951",
    lineHeight: 1,
    marginBottom: 7,
  },

  logoTagline: {
    fontSize: 12,
    color: "rgba(255,255,255,0.92)",
    marginBottom: 22,
  },

  newsLabel: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 34,
    padding: "0 18px",
    borderRadius: 999,
    border: "1px solid rgba(207,174,85,.34)",
    background: "rgba(255,255,255,.055)",
    color: "#f4d27a",
    fontSize: 16,
    fontWeight: 900,
    letterSpacing: "0.02em",
  },

  hero: {
    padding: "52px 64px 40px",
    textAlign: "center",
  },

  heroTitle: {
    margin: "0 auto 16px",
    maxWidth: 760,
    fontFamily: "var(--font-title, 'Playfair Display', serif)",
    fontSize: 46,
    lineHeight: 1.12,
    color: "#0f2f46",
  },

  heroSub: {
    margin: "0 auto 28px",
    maxWidth: 690,
    fontSize: 16,
    lineHeight: 1.65,
    color: "rgba(15,47,70,0.68)",
  },

  section: {
    padding: "0 64px 40px",
  },

  sectionSoft: {
    paddingTop: 32,
    paddingBottom: 34,
    background:
      "linear-gradient(180deg, rgba(15,47,70,0.035) 0%, rgba(255,255,255,0) 100%)",
    borderTop: "1px solid rgba(15,47,70,0.06)",
    borderBottom: "1px solid rgba(15,47,70,0.05)",
  },

  sectionFull: {
    display: "block",
  },

  sectionGrid: {
    display: "grid",
    gridTemplateColumns: "1.32fr 0.88fr",
    gap: 34,
    alignItems: "start",
  },

  sectionTitle: {
    margin: "0 0 18px",
    fontFamily: "var(--font-title, 'Playfair Display', serif)",
    fontSize: 30,
    lineHeight: 1.22,
    color: "#0f2f46",
  },

  leadText: {
    margin: "0 0 18px",
    fontSize: 18,
    lineHeight: 1.8,
    color: "rgba(15,47,70,0.82)",
  },

  bodyText: {
    margin: "0 0 18px",
    fontSize: 16.5,
    lineHeight: 1.82,
    color: "rgba(32,48,64,0.86)",
  },

  figure: {
    margin: "20px 0 0",
  },

  image: {
    width: "100%",
    display: "block",
    borderRadius: 20,
    boxShadow: "0 18px 36px rgba(15,47,70,0.12)",
    border: "1px solid rgba(15,47,70,.08)",
  },

  caption: {
    marginTop: 8,
    fontSize: 12,
    lineHeight: 1.45,
    color: "rgba(15,47,70,0.54)",
    textAlign: "center",
  },

  quoteBox: {
    margin: "24px auto",
    padding: "18px 22px",
    borderLeft: "3px solid #d6ad4f",
    background: "rgba(204,174,73,0.08)",
    color: "#0f2f46",
    fontFamily: "var(--font-title, 'Playfair Display', serif)",
    fontSize: 19,
    lineHeight: 1.45,
    borderRadius: "0 18px 18px 0",
  },

  cleanList: {
    margin: "18px 0 0",
    padding: "18px 20px 18px 38px",
    borderRadius: 18,
    background: "#ffffff",
    border: "1px solid rgba(15,47,70,0.08)",
    color: "rgba(32,48,64,0.88)",
    fontSize: 15.5,
    lineHeight: 1.75,
  },

  noteBox: {
    padding: "22px 24px 8px",
    borderRadius: 22,
    background: "rgba(15,47,70,0.045)",
    border: "1px solid rgba(15,47,70,0.08)",
  },

  ctaEditorialSection: {
    width: "min(720px, calc(100% - 128px))",
    margin: "4px auto 38px",
    padding: "24px 30px 26px",
    borderRadius: 24,
    background:
      "linear-gradient(135deg, rgba(15,47,70,0.96) 0%, rgba(23,63,91,0.96) 100%)",
    color: "#ffffff",
    border: "1px solid rgba(207,174,85,.26)",
    boxShadow: "0 20px 42px rgba(15,47,70,0.16)",
  },

  ctaText: {
    margin: "0 0 12px",
    fontSize: 15.5,
    lineHeight: 1.72,
    color: "rgba(255,255,255,0.88)",
  },

  ctaActions: {
    display: "flex",
    justifyContent: "center",
    marginTop: 18,
  },

  ctaButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 42,
    padding: "0 22px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #d7a951 0%, #f0cf75 100%)",
    color: "#0f2f46",
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 900,
    boxShadow: "0 14px 30px rgba(0,0,0,.14)",
  },

  footer: {
    padding: "30px 64px 36px",
    borderTop: "1px solid rgba(15,47,70,0.08)",
    background: "rgba(15,47,70,0.035)",
    textAlign: "center",
    fontSize: 12,
    lineHeight: 1.65,
    color: "rgba(15,47,70,0.58)",
  },

  footerBrand: {
    margin: "0 0 8px",
    fontFamily: "var(--font-title, 'Playfair Display', serif)",
    fontSize: 18,
    color: "#0f2f46",
  },

  kicker: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: ".16em",
    color: "#cfae55",
    fontWeight: 900,
    marginBottom: 8,
  },

  linkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 44,
    padding: "0 18px",
    borderRadius: 999,
    background: "#fff",
    color: "#0f2f46",
    textDecoration: "none",
    fontWeight: 900,
    whiteSpace: "nowrap",
    boxShadow: "0 10px 28px rgba(0,0,0,.12)",
  },

  statusPage: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: 22,
    background: "#f7f9fc",
  },

  statusCard: {
    width: "min(560px, 100%)",
    padding: 28,
    borderRadius: 24,
    background: "#fff",
    border: "1px solid rgba(44,59,81,.10)",
    boxShadow: "0 18px 44px rgba(15,47,70,.08)",
    textAlign: "center",
  },

  statusTitle: {
    margin: "0 0 10px",
    color: "#0f2f46",
    fontSize: "clamp(26px, 4vw, 38px)",
  },

  statusText: {
    margin: "0 0 18px",
    color: "rgba(44,59,81,.68)",
    lineHeight: 1.6,
  },
};
